import { AppShellHeader, Box, Burger, Container, Group, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconArrowLeft } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { ColorSchemeToggle } from '@/components/color-scheme-toggle';
import { useOrg } from '@/hooks/use-org';
import { useOrgWidgets } from '@/hooks/use-org-widgets';
import { AdminMenu } from './admin-menu';
import { NavDrawer } from './nav-drawer';
import { OrgSwitcher } from './org-switcher';
import { PhoneStatusIndicator } from './phone-status-indicator';
import { UserMenu } from './user-menu';
import { VoicemailBadge } from './voicemail-badge';
import { appNavLinks, isNavItemActive, systemNavLinks } from './nav-links';

export function BrandMark() {
  return (
    <Link to="/" style={{ textDecoration: 'none' }}>
      <Group gap="xs" wrap="nowrap">
        <Box
          w={32}
          h={32}
          style={{
            background: 'linear-gradient(135deg, var(--mantine-color-indigo-6), var(--mantine-color-violet-6))',
            borderRadius: 'var(--mantine-radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 700,
            fontSize: '14px',
            flexShrink: 0,
          }}
        >
          CD
        </Box>
        <Text fw={600} size="lg" c="var(--mantine-color-text)" visibleFrom="sm">
          CallDominator
        </Text>
      </Group>
    </Link>
  );
}

function ExitSystemLink() {
  return (
    <UnstyledButton
      component={Link}
      to="/"
      px="sm"
      py={6}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        borderRadius: 'var(--mantine-radius-md)',
        color: 'var(--mantine-color-dimmed)',
        fontWeight: 500,
        fontSize: 'var(--mantine-font-size-sm)',
        transition: 'background-color 150ms ease, color 150ms ease',
      }}
    >
      <IconArrowLeft size={16} stroke={1.5} />
      Exit System
    </UnstyledButton>
  );
}

export function Header({ pathname }: { pathname: string }) {
  const [drawerOpen, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const { currentOrg, organizationId, isPlatformOperatorView } = useOrg();
  const { hasAiAgent, hasContactCenter } = useOrgWidgets();

  const isSystemPage = pathname.startsWith('/system');
  const isOrgAdmin = currentOrg?.role === 'owner' || currentOrg?.role === 'admin' || isPlatformOperatorView;
  const navLinks = isSystemPage ? systemNavLinks : appNavLinks(hasAiAgent, hasContactCenter);
  const showAdminLinks = !isSystemPage && isOrgAdmin;

  return (
    <>
      <AppShellHeader
        className="cd-header-glass"
        style={{
          backgroundColor: 'var(--cd-header-bg)',
          borderBottom: '1px solid var(--cd-header-border)',
        }}
      >
        <Container fluid h="100%" px="lg">
          <Group h="100%" wrap="nowrap" gap="md">
            <Box style={{ flex: 1, display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
              <BrandMark />
            </Box>

            <Group gap={4} wrap="nowrap" visibleFrom="md">
              {navLinks.map((item) => {
                const isActive = isNavItemActive(pathname, item.link);

                return (
                  <UnstyledButton
                    key={item.label}
                    component={Link}
                    to={item.link}
                    px="sm"
                    py={6}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      borderRadius: 'var(--mantine-radius-md)',
                      backgroundColor: isActive ? 'var(--mantine-color-indigo-light)' : 'transparent',
                      color: isActive ? 'var(--mantine-color-indigo-filled)' : 'var(--mantine-color-dimmed)',
                      fontWeight: isActive ? 600 : 500,
                      fontSize: 'var(--mantine-font-size-sm)',
                      transition: 'background-color 150ms ease, color 150ms ease',
                    }}
                  >
                    <item.icon size={16} stroke={1.5} />
                    {item.label}
                    {item.link === '/voicemails' && <VoicemailBadge organizationId={organizationId} />}
                  </UnstyledButton>
                );
              })}
              {showAdminLinks && <AdminMenu pathname={pathname} organizationId={organizationId} />}
            </Group>

            <Box style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
              <Group gap="sm" wrap="nowrap">
                {hasContactCenter && !isSystemPage && <PhoneStatusIndicator />}
                <ColorSchemeToggle />
                {isSystemPage ? <ExitSystemLink /> : <OrgSwitcher />}
                <UserMenu />
                <Burger opened={drawerOpen} onClick={toggleDrawer} hiddenFrom="md" size="sm" />
              </Group>
            </Box>
          </Group>
        </Container>
      </AppShellHeader>

      <NavDrawer
        opened={drawerOpen}
        onClose={closeDrawer}
        navLinks={navLinks}
        pathname={pathname}
        organizationId={organizationId}
        showAdminLinks={showAdminLinks}
        isSystemPage={isSystemPage}
      />
    </>
  );
}
